import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Settings, PoundSterling, Headphones, TrendingUp, Check, Users, Globe, Award, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services, stats, testimonials, companyInfo, whyVisoShort } from '../data/mock';
import SEO from '../components/ui/SEO';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
  Settings,
  PoundSterling,
  Headphones,
  TrendingUp,
};

const HomePage = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const statsRef = useRef(null);
  const whyUsRef = useRef(null);
  const testimonialsRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Hero animations - staggered reveal
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      heroTl
        .fromTo('.hero-label',
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.7 }
        )
        .fromTo('.hero-headline',
          { opacity: 0, y: 50 },
          { opacity: 1, y: 0, duration: 0.9 },
          '-=0.4'
        )
        .fromTo('.hero-subtext',
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.7 },
          '-=0.5'
        )
        .fromTo('.hero-cta',
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6 },
          '-=0.3'
        )
        .fromTo('.hero-stats',
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.7 },
          '-=0.2'
        );

      // Services
      gsap.fromTo('.services-title',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: servicesRef.current, start: 'top 85%' },
        }
      );

      gsap.fromTo('.service-card',
        { opacity: 0, y: 50 },
        {
          opacity: 1, y: 0, duration: 0.6, stagger: 0.15, ease: 'power3.out',
          scrollTrigger: { trigger: '.services-grid', start: 'top 80%' },
        }
      );

      // Stats counter
      gsap.fromTo('.stat-item',
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1, scale: 1, duration: 0.6, stagger: 0.1, ease: 'power2.out',
          scrollTrigger: { trigger: statsRef.current, start: 'top 85%' },
        }
      );

      // Why us
      gsap.fromTo('.why-content',
        { opacity: 0, x: -50 },
        {
          opacity: 1, x: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: whyUsRef.current, start: 'top 80%' },
        }
      );

      gsap.fromTo('.why-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.5, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.why-cards', start: 'top 80%' },
        }
      );

      // Testimonials
      gsap.fromTo('.testimonial-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.6, stagger: 0.15, ease: 'power3.out',
          scrollTrigger: { trigger: testimonialsRef.current, start: 'top 80%' },
        }
      );

      // CTA
      gsap.fromTo('.cta-content',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: ctaRef.current, start: 'top 85%' },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="People Powering Your Business Growth"
        description="VISO Connect provides expert operational support for growing businesses — customer service, bookkeeping, virtual assistance, and more. Trusted by 50+ clients."
        canonical="https://visoconnect.in/"
      />
      <div className="overflow-hidden bg-[#0a0a0f]">
        {/* ========== HERO SECTION ========== */}
        <section ref={heroRef} className="hero-dark relative min-h-screen flex items-center pt-24 pb-20">
          {/* Ambient glow effects */}
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-4xl">
              {/* Label */}
              <div className="hero-label inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-8">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-slate-300">Trusted by Growing Businesses</span>
              </div>

              {/* Headline */}
              <h1 className="hero-headline text-white mb-6" style={{
                fontSize: 'clamp(2.75rem, 7vw, 5rem)',
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: '-0.03em'
              }}>
                People{' '}
                <span className="gradient-text">powering</span>
                <br />
                your business growth
              </h1>

              {/* Subtext */}
              <p className="hero-subtext text-slate-400 mb-10 leading-relaxed" style={{
                fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
                maxWidth: '580px'
              }}>
                {companyInfo.description}
              </p>

              {/* CTAs */}
              <div className="hero-cta flex flex-wrap items-center gap-4 mb-16">
                <Link to="/contact">
                  <Button className="btn-glow bg-gradient-to-r from-indigo-500 to-cyan-500 text-white px-8 py-6 text-base font-semibold rounded-xl border-0">
                    Speak with an Expert
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base font-medium rounded-xl">
                    Explore Services
                  </Button>
                </Link>
              </div>

              {/* Hero Stats */}
              <div className="hero-stats grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  { value: '50+', label: 'Clients Served' },
                  { value: '99%', label: 'Client Satisfaction Rate' },
                  { value: '3+', label: 'Years of Experience' },
                  { value: '24/7', label: 'Support Availability' },
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>
                      {stat.value}
                    </div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========== SERVICES SECTION ========== */}
        <section ref={servicesRef} className="py-28 bg-[#111118]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section Header */}
            <div className="services-title text-center mb-16">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                What We Do
              </span>
              <h2 className="text-white mb-5" style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
                Comprehensive Business Support
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                We bring together skilled teams and proven processes to deliver reliable operations support.
              </p>
            </div>

            {/* Services Grid */}
            <div className="services-grid grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon];
                return (
                  <div
                    key={service.id}
                    className="service-card card-dark p-8 group cursor-pointer"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:from-indigo-500/30 group-hover:to-cyan-500/30 transition-all duration-300">
                      {IconComponent && <IconComponent className="text-indigo-400" size={28} />}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5" style={{ maxWidth: '380px' }}>
                      {service.shortDescription}
                    </p>
                    <Link
                      to={`/services#${service.id}`}
                      className="inline-flex items-center text-indigo-400 text-sm font-medium hover:text-indigo-300 transition-colors"
                    >
                      Learn more
                      <ArrowRight size={16} className="ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========== STATS SECTION ========== */}
        <section ref={statsRef} className="py-20 bg-gradient-to-r from-indigo-600 to-cyan-600 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {stats.map((stat) => (
                <div key={stat.id} className="stat-item text-center">
                  <div className="stat-number text-white mb-2">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-white/80 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== WHY VISO CONNECT ========== */}
        <section ref={whyUsRef} className="py-28 bg-[#0a0a0f]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left Content */}
              <div className="why-content">
                <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                  Why VISO Connect
                </span>
                <h2 className="text-white mb-6" style={{
                  fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                  Built for{' '}
                  <span className="gradient-text">reliability</span>
                  {' '}and results
                </h2>
                <p className="text-slate-400 leading-relaxed mb-8" style={{ maxWidth: '480px' }}>
                  We combine experienced teams with proven processes to deliver consistent, measurable outcomes for your business.
                </p>

                {/* Feature bullets */}
                <div className="space-y-4 mb-8">
                  {whyVisoShort.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="text-emerald-400" size={14} strokeWidth={3} />
                      </div>
                      <span className="text-slate-300 font-medium">{item}</span>
                    </div>
                  ))}
                </div>

                <Link to="/about">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-xl">
                    About Us
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
              </div>

              {/* Right Cards */}
              <div className="why-cards grid grid-cols-2 gap-4">
                {[
                  { icon: Users, title: 'Dedicated Teams', desc: 'Focused teams who understand your business' },
                  { icon: Settings, title: 'Process-Driven', desc: 'Consistent delivery through proven processes' },
                  { icon: Globe, title: 'Scalable Models', desc: 'Flexible engagement that grows with you' },
                  { icon: Award, title: 'Clear Reporting', desc: 'Transparent metrics and accountability' },
                ].map((item, i) => (
                  <div key={i} className="why-card card-dark p-6 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <item.icon className="text-indigo-400" size={24} />
                    </div>
                    <h3 className="text-white font-semibold mb-2 text-sm">{item.title}</h3>
                    <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========== TESTIMONIALS ========== */}
        <section ref={testimonialsRef} className="py-28 bg-[#111118]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                Client Stories
              </span>
              <h2 className="text-white" style={{
                fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
                Trusted by businesses like yours
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="testimonial-card card-dark p-8">
                  {/* Quote */}
                  <div className="mb-6">
                    <svg className="w-10 h-10 text-indigo-500/30" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <blockquote className="text-slate-300 leading-relaxed mb-6">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <div className="font-semibold text-white text-sm">{testimonial.author}</div>
                    <div className="text-slate-500 text-xs">{testimonial.role}, {testimonial.company}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== CTA SECTION ========== */}
        <section ref={ctaRef} className="py-28 bg-[#0a0a0f] relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center cta-content">
            <h2 className="text-white mb-6" style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
              Ready to scale your operations?
            </h2>
            <p className="text-slate-400 mb-10 mx-auto" style={{ maxWidth: '480px' }}>
              Let's discuss how VISO Connect can support your business growth with reliable, expert teams.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-glow bg-gradient-to-r from-indigo-500 to-cyan-500 text-white px-8 py-6 text-base font-semibold rounded-xl border-0">
                  Get Started
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base font-medium rounded-xl">
                  View Services
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
